import { useEffect, useRef, useState } from 'react';
import type { RefObject, UIEvent } from 'react';

import { toast } from 'sonner';
// package.json pins SheetJS to their own CDN tarball; the npm `xlsx` package is stale.
import * as XLSX from 'xlsx';

import { cn } from '@/lib/utils';

interface ExcelPreviewPaneProps {
    /** Null skips the fetch and renders the fallback, like `PdfPreviewPane`'s null docId. */
    presignedUrl: string | null;
    /** Rendered when there's no URL, or when the fetch/parse fails. */
    renderFallback: () => React.ReactNode;
    /** `data-testid` for the loading state. */
    loadingTestId?: string;
    /** `data-testid` for the rendered table wrapper. */
    tableTestId?: string;
    /** Called with the workbook's sheet count and sheet names once it's loaded. */
    onLoad?: (sheetCount: number, sheetNames: string[]) => void;
    /** Called when the workbook could not be fetched or parsed. */
    onLoadError?: () => void;
    /** Add column letters and row numbers (real Excel positions) to the grid. */
    showCellReferences?: boolean;
    /** Show the sheet tab bar even for a single-sheet workbook. */
    alwaysShowTabs?: boolean;
    /** Show only the sheet with this name, with no tab bar. Wins over `sheetIndex`. */
    sheetName?: string | null;
    /**
     * Show only the sheet at this 0-based position in the workbook, hidden sheets
     * counted, with no tab bar. Used when `sheetName` is absent or not found.
     */
    sheetIndex?: number | null;
    /** The table wrapper is the element that scrolls, not any container wrapped around it. */
    scrollContainerRef?: RefObject<HTMLDivElement | null>;
    onScroll?: (event: UIEvent<HTMLDivElement>) => void;
}

interface LoadedWorkbook {
    sheetNames: string[];
    sheets: Record<string, XLSX.WorkSheet>;
}

/**
 * Workbooks can declare a `!ref` far bigger than their populated cells (whole-column
 * formatting inflates it to 1,048,576 rows), and `sheet_to_html` walks the whole
 * declared range and throws on the joined string. Trim it to the cells that exist.
 */
export function trimSheetToPopulatedRange(
    sheet: XLSX.WorkSheet,
): XLSX.WorkSheet {
    let minRow = Infinity;
    let minCol = Infinity;
    let maxRow = -1;
    let maxCol = -1;

    for (const key of Object.keys(sheet)) {
        if (key.startsWith('!')) continue;

        const { r, c } = XLSX.utils.decode_cell(key);
        if (r < minRow) minRow = r;
        if (c < minCol) minCol = c;
        if (r > maxRow) maxRow = r;
        if (c > maxCol) maxCol = c;
    }

    if (maxRow < 0 || maxCol < 0) {
        // An empty sheet still carries an inflated `!ref`, so clamp it to one cell.
        // No cells means no valid merges either.
        const { '!merges': _merges, ...rest } = sheet;
        return {
            ...rest,
            '!ref': XLSX.utils.encode_range({
                s: { r: 0, c: 0 },
                e: { r: 0, c: 0 },
            }),
        };
    }

    // Bound where the data starts, not just where it ends: one cell at A500000 would
    // otherwise still declare a half-million-row range.
    const trimmed: XLSX.WorkSheet = {
        ...sheet,
        '!ref': XLSX.utils.encode_range({
            s: { r: minRow, c: minCol },
            e: { r: maxRow, c: maxCol },
        }),
    };

    if (trimmed['!merges']) {
        trimmed['!merges'] = trimmed['!merges']
            .map((merge) => ({
                s: {
                    r: Math.min(Math.max(merge.s.r, minRow), maxRow),
                    c: Math.min(Math.max(merge.s.c, minCol), maxCol),
                },
                e: {
                    r: Math.min(Math.max(merge.e.r, minRow), maxRow),
                    c: Math.min(Math.max(merge.e.c, minCol), maxCol),
                },
            }))
            .filter(
                (merge) => merge.s.r <= merge.e.r && merge.s.c <= merge.e.c,
            );
    }

    return trimmed;
}

/** Insert a column-letter header row and a row-number cell, using the trimmed range's real start. */
function addCellReferences(html: string, range: XLSX.Range): string {
    const columnCount = range.e.c - range.s.c + 1;
    const headerCells = Array.from(
        { length: columnCount },
        (_, i) =>
            `<th class="excel-col-ref">${XLSX.utils.encode_col(range.s.c + i)}</th>`,
    ).join('');
    const header = `<tr><th class="excel-corner-ref"></th>${headerCells}</tr>`;

    let rowNumber = range.s.r;
    const withRowNumbers = html.replace(
        /<tr(\s[^>]*)?>/g,
        (tag) => `${tag}<th class="excel-row-ref">${++rowNumber}</th>`,
    );

    return withRowNumbers.replace(/<table[^>]*>/, (tag) => `${tag}${header}`);
}

/** `SheetNames` includes hidden sheets, so an index here counts them. */
function resolvePinnedSheetName(
    sheetNames: string[],
    sheetName: string | null,
    sheetIndex: number | null,
): string | undefined {
    if (sheetName !== null && sheetNames.includes(sheetName)) return sheetName;
    if (sheetIndex !== null) return sheetNames[sheetIndex];
    return undefined;
}

/** Read-only workbook preview: the active sheet as a table, with a tab per sheet. */
export function ExcelPreviewPane({
    presignedUrl,
    renderFallback,
    loadingTestId,
    tableTestId,
    onLoad,
    onLoadError,
    showCellReferences = false,
    alwaysShowTabs = false,
    sheetName = null,
    sheetIndex = null,
    scrollContainerRef,
    onScroll,
}: ExcelPreviewPaneProps) {
    const [workbook, setWorkbook] = useState<LoadedWorkbook | null>(null);
    const [isLoading, setIsLoading] = useState(presignedUrl !== null);
    const [hasError, setHasError] = useState(false);
    const [activeSheetIndex, setActiveSheetIndex] = useState(0);
    // A ref, so a caller passing a fresh onLoad each render does not refetch.
    const onLoadRef = useRef(onLoad);
    onLoadRef.current = onLoad;
    const onLoadErrorRef = useRef(onLoadError);
    onLoadErrorRef.current = onLoadError;

    useEffect(() => {
        if (presignedUrl === null) {
            setWorkbook(null);
            setIsLoading(false);
            setHasError(false);
            return;
        }

        let isCurrent = true;
        setIsLoading(true);
        setHasError(false);
        setWorkbook(null);
        setActiveSheetIndex(0);

        async function loadWorkbook() {
            const response = await fetch(presignedUrl as string);
            if (!response.ok) {
                throw new Error(
                    `Failed to fetch workbook (${response.status})`,
                );
            }

            const buffer = await response.arrayBuffer();
            const parsed = XLSX.read(buffer, { type: 'array' });

            if (!isCurrent) return;

            setWorkbook({
                sheetNames: parsed.SheetNames,
                sheets: parsed.Sheets,
            });
            setIsLoading(false);
            onLoadRef.current?.(parsed.SheetNames.length, parsed.SheetNames);
        }

        loadWorkbook().catch((error: unknown) => {
            if (!isCurrent) return;

            // Surfaced so a real parse bug is not mistaken for a file with no preview.
            console.error('Failed to load Excel preview', error);
            toast.error('Failed to load Excel preview');
            setHasError(true);
            setIsLoading(false);
            onLoadErrorRef.current?.();
        });

        return () => {
            isCurrent = false;
        };
    }, [presignedUrl]);

    const isPinned = sheetName !== null || sheetIndex !== null;
    // A pinned sheet that cannot be found is a failure, not a licence to show another one.
    const pinnedSheetName =
        isPinned && workbook !== null
            ? resolvePinnedSheetName(workbook.sheetNames, sheetName, sheetIndex)
            : undefined;
    const isPinnedSheetMissing =
        isPinned && workbook !== null && pinnedSheetName === undefined;

    useEffect(() => {
        if (!isPinnedSheetMissing) return;
        console.error('Pinned sheet not found in workbook', {
            sheetName,
            sheetIndex,
        });
        toast.error('Failed to load Excel preview');
        onLoadErrorRef.current?.();
    }, [isPinnedSheetMissing, sheetName, sheetIndex]);

    if (presignedUrl === null) {
        return <>{renderFallback()}</>;
    }

    if (isLoading) {
        return (
            <div
                className="flex items-center justify-center h-full text-sm text-muted-foreground"
                data-testid={loadingTestId}
            >
                Loading preview…
            </div>
        );
    }

    if (hasError || workbook === null) {
        return <>{renderFallback()}</>;
    }

    if (isPinnedSheetMissing) {
        return <>{renderFallback()}</>;
    }

    const activeSheetName =
        pinnedSheetName ??
        workbook.sheetNames[activeSheetIndex] ??
        workbook.sheetNames[0];
    const activeSheet = activeSheetName
        ? workbook.sheets[activeSheetName]
        : undefined;

    let activeSheetHtml: string;
    try {
        if (activeSheet) {
            const trimmed = trimSheetToPopulatedRange(activeSheet);
            const html = XLSX.utils.sheet_to_html(trimmed);
            activeSheetHtml = showCellReferences
                ? addCellReferences(
                      html,
                      XLSX.utils.decode_range(trimmed['!ref'] as string),
                  )
                : html;
        } else {
            activeSheetHtml = '';
        }
    } catch (error) {
        // A `!ref` we failed to trim must not take the page down with it.
        console.error('Failed to render Excel preview', error);
        toast.error('Failed to render Excel preview');
        return <>{renderFallback()}</>;
    }

    return (
        <div
            className="flex h-full min-h-0 w-full min-w-0 flex-col"
            data-testid={tableTestId}
        >
            {!isPinned && (alwaysShowTabs || workbook.sheetNames.length > 1) && (
                <div className="flex flex-none gap-1 overflow-x-auto border-b bg-muted/40 p-2">
                    {workbook.sheetNames.map((sheetName, index) => (
                        <button
                            key={sheetName}
                            type="button"
                            onClick={() => setActiveSheetIndex(index)}
                            className={
                                index === activeSheetIndex
                                    ? 'shrink-0 whitespace-nowrap rounded px-2 py-1 text-xs font-medium bg-background shadow-xs'
                                    : 'shrink-0 whitespace-nowrap rounded px-2 py-1 text-xs text-muted-foreground hover:bg-background/60'
                            }
                        >
                            {sheetName}
                        </button>
                    ))}
                </div>
            )}
            {/*
                Only safe because presignedUrl points at the viewer's own tenant's file.
                Revisit sanitising if a caller ever shows another tenant's data.
            */}
            <div
                ref={scrollContainerRef}
                onScroll={onScroll}
                // Focusable so keyboard users can scroll a sheet with no focusable cells.
                tabIndex={0}
                role="region"
                aria-label={`Sheet ${activeSheetName ?? ''}`.trim()}
                // Sticky refs stick inside the padding, so padding on their edges lets
                // rows show through above and beside them.
                className={cn(
                    showCellReferences ? 'pb-2 pr-2' : 'p-2',
                    'min-h-0 flex-1 overflow-auto [&_table]:border-collapse [&_td]:border [&_td]:border-border [&_td]:px-2 [&_td]:py-1 [&_th]:border [&_th]:border-border [&_th]:px-2 [&_th]:py-1 [&_.excel-col-ref]:sticky [&_.excel-col-ref]:top-0 [&_.excel-col-ref]:z-[1] [&_.excel-col-ref]:bg-muted [&_.excel-col-ref]:text-xs [&_.excel-col-ref]:font-medium [&_.excel-col-ref]:text-muted-foreground [&_.excel-corner-ref]:sticky [&_.excel-corner-ref]:left-0 [&_.excel-corner-ref]:top-0 [&_.excel-corner-ref]:z-10 [&_.excel-corner-ref]:bg-muted [&_.excel-row-ref]:sticky [&_.excel-row-ref]:left-0 [&_.excel-row-ref]:bg-muted [&_.excel-row-ref]:text-xs [&_.excel-row-ref]:font-medium [&_.excel-row-ref]:text-muted-foreground',
                )}
                dangerouslySetInnerHTML={{ __html: activeSheetHtml }}
            />
        </div>
    );
}
